import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '../icons';

/** Perché compare la richiesta: si dice sempre all'utente. */
export type LockReason = { kind: 'avvio' } | { kind: 'aggiornato'; version: string } | { kind: 'assenza'; minutes: number };

function why(r: LockReason): string {
  if (r.kind === 'aggiornato') return 'CRM aggiornato';
  if (r.kind === 'avvio') return 'CRM appena aperto';
  if (r.minutes >= 120) return `Sei stato via ${Math.round(r.minutes / 60)} ore`;
  if (r.minutes >= 60) return 'Sei stato via più di un’ora';
  return `Sei stato via ${r.minutes} minuti`;
}

interface Props {
  name?: string;
  email?: string;
  version: string;
  reason: LockReason;
  /** Sblocco riuscito: si entra con l'accesso già salvato su questo PC. */
  onUnlocked: () => void;
  /** Rinuncia allo sblocco: si esce e si torna a email e password. */
  onUsePassword: () => void;
}

/** Schermata di sblocco con impronta digitale o riconoscimento del viso (Windows Hello). */
export function Lock({ name, email, version, reason, onUnlocked, onUsePassword }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unlock = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await window.tt.helloVerify('Sblocca CRM TecnoSide');
      if (r.ok) onUnlocked();
      else setError(r.status === 'canceled' ? null : r.message);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [onUnlocked]);

  // La richiesta di Windows parte da sola, ma solo quando la finestra è davvero in vista:
  // all'accensione del PC l'app sta nell'area di notifica e non deve disturbare nessuno.
  useEffect(() => {
    if (document.visibilityState === 'visible') {
      unlock();
      return;
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', onVisible);
        unlock();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [unlock]);

  return (
    <div className="login lock-overlay">
      <div className="login-card lock-card">
        <div className="lock-ring">
          <Icon name="fingerprint" size={46} strokeWidth={1.5} />
        </div>
        <h1 style={{ margin: 0 }}>{name ? `Bentornato, ${name.split(' ')[0]}` : 'CRM TecnoSide'}</h1>
        <div className="lock-why" title={email ? `Accesso di ${email}` : undefined}>
          {why(reason)}: conferma che sei tu.
        </div>

        {error && <div className="error" style={{ marginTop: 4 }}>{error}</div>}

        <button className="btn dark block" type="button" onClick={unlock} disabled={busy} style={{ marginTop: 16, padding: '12px 16px', fontSize: 15 }}>
          <Icon name={busy ? 'clock' : 'fingerprint'} size={18} /> {busy ? 'Appoggia il dito o guarda la fotocamera…' : 'Sblocca con impronta o viso'}
        </button>
        <button
          className="btn ghost block"
          type="button"
          onClick={onUsePassword}
          disabled={busy}
          style={{ marginTop: 8 }}
          title="Chiude il CRM su questo PC: poi rientri con Google. La richiesta dell’impronta si spegne da Impostazioni."
        >
          <Icon name="logout" size={15} /> Esci
        </button>
        <div className="muted tiny" style={{ textAlign: 'center', marginTop: 10 }}>
          v{version}
        </div>
      </div>
    </div>
  );
}
